import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  endpoints,
  type CompetitorSponsorshipItem,
} from "@/lib/api";
import { fmtDate, fmtNumber } from "@/lib/format";
import { Badge, decisionVariant, decisionLabel } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";

// ChannelSponsorshipsSection — v2.4.8 — extracted from ChannelDetail.tsx
// (was 1319 LOC). The competitor-推广 record is one of the longest
// self-contained sections; pulling it out drops the route file by ~240
// LOC without any behavior change. Pure presentational, takes channelID
// as prop, fetches its own data.
//
// Was previously named CompetitorSponsorshipsSection in the route file;
// kept the longer name `ChannelSponsorshipsSection` in this export so
// imports read more naturally outside ChannelDetail's context.
export function ChannelSponsorshipsSection({ channelID }: { channelID: number }) {
  const [days, setDays] = useState(90);
  const [includePending, setIncludePending] = useState(true);
  const q = useQuery({
    queryKey: ["competitor-sponsorships", channelID, days, includePending],
    queryFn: () => endpoints.channelCompetitorSponsorships(channelID, days, includePending),
    staleTime: 30_000,
  });

  // Group sponsorship rows by content (video/tweet). Same content can have
  // N exchanges (e.g. an airdrop-listing tweet mentioning Binance + OKX +
  // Bybit + ...) — we want one card per content with the exchange chips
  // inside, not N near-duplicate cards.
  const grouped = useMemo(() => groupByVideo(q.data ?? []), [q.data]);
  const exchangeCounts = useMemo(() => countByExchange(q.data ?? []), [q.data]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">
          竞品推广记录
          {q.data && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              ({q.data.length} 条 · 涉及 {grouped.length} 条内容 · 过去 {days} 天)
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2 text-xs">
          <Select
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-7 w-24 text-xs"
          >
            <option value="30">30 天</option>
            <option value="90">90 天</option>
            <option value="180">180 天</option>
            <option value="365">365 天</option>
          </Select>
          <label className="flex items-center gap-1 text-muted-foreground">
            <input
              type="checkbox"
              checked={includePending}
              onChange={(e) => setIncludePending(e.target.checked)}
              className="h-3 w-3"
            />
            含 pending
          </label>
        </div>
      </div>
      {q.isLoading && <p className="text-xs text-muted-foreground">加载中…</p>}
      {q.data && q.data.length === 0 && (
        <p className="text-xs text-muted-foreground">
          过去 {days} 天没有竞品推广记录
          {!includePending && "（试试勾选'含 pending'看未审核的）"}
        </p>
      )}
      {q.data && q.data.length > 0 && (
        <>
          {/* Summary card: exchange frequency + dedicated/shared split.
              Helps the operator see at a glance whether this KOL leans
              toward one exchange or spreads evenly, AND whether the
              promotion is dedicated content or part of a multi-exchange
              roundup. Note: both kinds still count for the exchange's
              exposure — the split is informational, not a weighting. */}
          <div className="mb-3 rounded border border-border bg-muted/30 p-3">
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              推过的交易所(过去 {days} 天,按推广次数排序):
            </div>
            <div className="flex flex-wrap gap-1.5">
              {exchangeCounts.map((ec) => (
                <Badge key={ec.code} variant="secondary" className="font-normal">
                  {ec.name} <span className="ml-1 text-muted-foreground">×{ec.count}</span>
                </Badge>
              ))}
            </div>
            {(() => {
              const dedicated = grouped.filter((g) => g.coOccurring === 1).length;
              const shared = grouped.length - dedicated;
              if (grouped.length === 0) return null;
              return (
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                  <span>
                    内容形态:<b className="text-foreground">独家推广 {dedicated}</b> ·{" "}
                    <b className="text-foreground">共现 {shared}</b>
                  </span>
                  {shared > dedicated && (
                    <span className="text-amber-600">
                      共现 &gt; 独家 —— 这 KOL 更像"空投/汇总"内容,而非定向代言
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Grouped list: one card per content. */}
          <ul className="space-y-2">
            {grouped.map((g) => (
              <li
                key={g.videoID}
                className="rounded border border-border bg-muted/20 p-3 text-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="line-clamp-2 flex-1 font-medium">
                        {g.videoURL ? (
                          <a
                            href={g.videoURL}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                            title={g.videoTitle}
                          >
                            {g.videoTitle ?? `Video #${g.videoID}`}
                          </a>
                        ) : (
                          g.videoTitle ?? `Video #${g.videoID}`
                        )}
                      </div>
                      <Badge
                        variant={g.coOccurring === 1 ? "info" : "outline"}
                        className="shrink-0 text-[10px] font-normal"
                      >
                        {g.coOccurring === 1
                          ? "独家"
                          : `共现 ${g.coOccurring} 家`}
                      </Badge>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>发布 {fmtDate(g.publishedAt)}</span>
                      {g.viewCount != null && g.viewCount > 0 && (
                        <span>· {fmtNumber(g.viewCount)} 观看</span>
                      )}
                      <span>· {g.detectionMethod}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {g.items.map((it) => (
                        <Badge
                          key={it.sponsorship_id}
                          variant={decisionVariant(it.review_status)}
                          className="text-[10px] font-normal"
                          title={`置信度 ${it.confidence}`}
                        >
                          {it.exchange_display_name} ·{" "}
                          {decisionLabel(it.review_status)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {g.pendingCount > 0 && (
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <Link
                        to="/sponsorships"
                        className="text-[10px] text-primary hover:underline"
                      >
                        去审核 {g.pendingCount} 条 →
                      </Link>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// Group sponsorship items by video. Sort by publishedAt desc (newest first).
function groupByVideo(items: CompetitorSponsorshipItem[]): GroupedSponsorship[] {
  const byVideo = new Map<number, GroupedSponsorship>();
  for (const it of items) {
    let g = byVideo.get(it.video_id);
    if (!g) {
      g = {
        videoID: it.video_id,
        videoTitle: it.video_title,
        videoURL: it.video_url,
        publishedAt: it.video_published_at,
        viewCount: it.video_view_count,
        detectionMethod: it.detection_method,
        items: [],
        pendingCount: 0,
        coOccurring: it.co_occurring_exchanges,
      };
      byVideo.set(it.video_id, g);
    }
    g.items.push(it);
    if (it.review_status === "pending") g.pendingCount++;
  }
  // Stable order inside each video: confirmed first, then pending, then
  // rejected. Helps the eye scan "what's been decided about this content".
  const statusRank: Record<string, number> = { confirmed: 0, pending: 1, rejected: 2 };
  return Array.from(byVideo.values())
    .map((g) => ({
      ...g,
      items: [...g.items].sort(
        (a, b) =>
          (statusRank[a.review_status] ?? 9) - (statusRank[b.review_status] ?? 9),
      ),
    }))
    .sort((a, b) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""));
}

interface GroupedSponsorship {
  videoID: number;
  videoTitle?: string;
  videoURL?: string;
  publishedAt?: string;
  viewCount?: number;
  detectionMethod: string;
  items: CompetitorSponsorshipItem[];
  pendingCount: number;
  coOccurring: number;
}

// Tally each exchange's appearance count. Sorted desc so the dominant
// exchange shows first.
function countByExchange(
  items: CompetitorSponsorshipItem[],
): { code: string; name: string; count: number }[] {
  const map = new Map<string, { name: string; count: number }>();
  for (const it of items) {
    const cur = map.get(it.exchange_code);
    if (cur) cur.count++;
    else map.set(it.exchange_code, { name: it.exchange_display_name, count: 1 });
  }
  return Array.from(map.entries())
    .map(([code, v]) => ({ code, ...v }))
    .sort((a, b) => b.count - a.count);
}
